import {DatePipe} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {TranslateService} from '@ngx-translate/core';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {GateweeConfirmDialogComponent} from '../gatewee-confirm-dialog/gatewee-confirm-dialog.component';
import {RecipientPixKey} from '@gatewee/types/recipient-pix-keys';
import {DocumentPipe} from '@gatewee/utils/pipes/document.pipe';

export interface PixKeyActions {
  onRetrieveKeys?: () => Observable<void>;
  onDisable?: (id: number) => Observable<void>;
  onUpdateDescription?: (
    id: number,
    description: string | undefined,
  ) => Observable<void>;
  onView?: (key: RecipientPixKey) => void;
  loadKeys?: () => void;
}

type RecipientPixKeyTableRow = RecipientPixKey & {bankCodeDisplay: string};

@Component({
  selector: 'gatewee-pix-keys',
  templateUrl: './gatewee-pix-keys.component.html',
  styleUrls: ['./gatewee-pix-keys.component.css'],
  standalone: false,
  providers: [DatePipe, DocumentPipe],
})
export class GateweePixKeysComponent implements OnInit, OnChanges {
  @Input() pixKeys: RecipientPixKey[] = [];
  @Input() actions!: PixKeyActions;
  @Input() disableContentPage = false;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() title = 'features.pixKeys.title';
  @Input() titlePath = 'features.pixKeys.titlePath';
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() total = 0;
  @Output() pageChange = new EventEmitter<number>();

  @ViewChild('keyViewModal') keyViewModal!: TemplateRef<never>;
  @ViewChild('editDescriptionModal') editDescriptionModal!: TemplateRef<never>;

  columns: GateweeTableColumn[] = [];
  tablePixKeys: RecipientPixKeyTableRow[] = [];
  editForm: FormGroup;
  selectedKey: RecipientPixKey | null = null;
  loading = false;

  private keyViewModalSub?: Subscription;
  private editDescriptionModalSub?: Subscription;

  constructor(
    private dialogService: TuiDialogService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private documentPipe: DocumentPipe,
    private translate: TranslateService,
  ) {
    this.editForm = this.fb.group({
      description: ['', [Validators.maxLength(255)]],
    });
  }

  ngOnInit(): void {
    this.setupColumns();
    this.rebuildTablePixKeys();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pixKeys']) {
      this.rebuildTablePixKeys();
    }
  }

  private rebuildTablePixKeys(): void {
    this.tablePixKeys = (this.pixKeys ?? []).map(key => ({
      ...key,
      bankCodeDisplay: this.getBankCodeDisplay(key),
    }));
  }

  private setupColumns(): void {
    const rowActions: GateweeTableColumn['actions'] = [
      {key: 'view', label: 'common.actions.view', icon: '@tui.eye'},
    ];

    if (this.actions?.onUpdateDescription) {
      rowActions.push({
        key: 'edit',
        label: 'common.actions.edit',
        icon: '@tui.pencil',
      });
    }

    if (this.actions?.onDisable) {
      rowActions.push({
        key: 'delete',
        label: 'features.pixKeys.table.menu.disable',
        icon: '@tui.ban',
        danger: true,
      });
    }

    const actionCol: GateweeTableColumn = {
      label: 'features.pixKeys.table.columns.actions',
      field: 'actions',
      sortable: false,
      visible: true,
      alwaysVisible: true,
      actions: rowActions,
    };

    this.columns = [
      {
        label: 'features.pixKeys.table.columns.id',
        field: 'id',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.pixKeys.table.columns.key',
        field: 'key',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.pixKeys.table.columns.description',
        field: 'description',
        sortable: false,
        visible: true,
        pipe: {
          fn: {
            transform: (v: unknown) =>
              v !== null && v !== undefined && String(v).trim() !== ''
                ? String(v)
                : '–',
          },
          args: [],
        },
      },
      {
        label: 'features.pixKeys.table.columns.document',
        field: 'document',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.documentPipe,
          args: [true],
        },
      },
      {
        label: 'features.pixKeys.table.columns.bank',
        field: 'bankCodeDisplay',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.pixKeys.table.columns.agency',
        field: 'agency',
        sortable: false,
        visible: true,
        pipe: {
          fn: {
            transform: (v: unknown) =>
              v !== null && v !== undefined ? String(v) : '–',
          },
          args: [],
        },
      },
      {
        label: 'features.pixKeys.table.columns.account',
        field: 'account',
        sortable: false,
        visible: true,
        pipe: {
          fn: {
            transform: (v: unknown) =>
              v !== null && v !== undefined ? String(v) : '–',
          },
          args: [],
        },
      },
      {
        label: 'features.pixKeys.table.columns.createdAt',
        field: 'createdAt',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.datePipe,
          args: ['dd/MM/yyyy HH:mm'],
        },
      },
      actionCol,
    ];
  }

  onActionClicked(event: {row: RecipientPixKey; type: string}): void {
    const {row, type} = event;
    switch (type) {
      case 'view':
        this.openKeyView(row);
        break;
      case 'edit':
        this.openEditDescription(row);
        break;
      case 'delete':
        this.handleDisable(row.id);
        break;
      default:
        break;
    }
  }

  openKeyView(key: RecipientPixKey): void {
    if (this.actions.onView) {
      this.actions.onView(key);
      return;
    }
    this.selectedKey = key;
    this.keyViewModalSub?.unsubscribe();
    this.keyViewModalSub = this.dialogService
      .open(this.keyViewModal, {
        size: 'l',
        closeable: true,
      })
      .subscribe({
        complete: () => {
          this.keyViewModalSub = undefined;
          this.selectedKey = null;
        },
      });
  }

  openEditDescription(key: RecipientPixKey): void {
    if (!this.actions.onUpdateDescription) return;
    this.selectedKey = key;
    this.editForm.reset({description: key.description ?? ''});
    this.editDescriptionModalSub?.unsubscribe();
    this.editDescriptionModalSub = this.dialogService
      .open(this.editDescriptionModal, {
        size: 'm',
        closeable: true,
      })
      .subscribe({
        complete: () => {
          this.editDescriptionModalSub = undefined;
          this.selectedKey = null;
          this.editForm.reset({description: ''});
        },
      });
  }

  openEditFromView(close: unknown): void {
    if (!this.selectedKey) return;
    const key = this.selectedKey;
    this.closeModal(close);
    setTimeout(() => this.openEditDescription(key), 0);
  }

  submitDescriptionEdit(close: unknown): void {
    if (
      this.editForm.invalid ||
      !this.selectedKey ||
      !this.actions.onUpdateDescription
    ) {
      this.editForm.markAllAsTouched();
      return;
    }

    const raw = (this.editForm.get('description')?.value as string) ?? '';
    const trimmed = raw.trim();
    const description = trimmed.length > 0 ? trimmed : undefined;

    this.loading = true;
    this.actions
      .onUpdateDescription(this.selectedKey.id, description)
      .subscribe({
        next: () => {
          this.loading = false;
          this.actions.loadKeys?.();
          this.closeModal(close);
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  closeModal(close: unknown): void {
    if (typeof close === 'function') {
      close();
    } else if (
      close &&
      typeof close === 'object' &&
      'complete' in close &&
      typeof (close as {complete?: () => void}).complete === 'function'
    ) {
      (close as {complete: () => void}).complete();
    }
    this.editDescriptionModalSub?.unsubscribe();
    this.editDescriptionModalSub = undefined;
    this.keyViewModalSub?.unsubscribe();
    this.keyViewModalSub = undefined;
  }

  onRetrieveKeys(): void {
    if (!this.actions.onRetrieveKeys) return;
    this.loading = true;
    this.actions.onRetrieveKeys().subscribe({
      next: () => {
        this.loading = false;
        this.actions.loadKeys?.();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  handleDisable(id: number): void {
    if (!this.actions.onDisable) return;
    this.dialogService
      .open<boolean>(new PolymorpheusComponent(GateweeConfirmDialogComponent), {
        data: {
          title: this.translate.instant(
            'features.pixKeys.disableConfirm.title',
          ),
          message: this.translate.instant(
            'features.pixKeys.disableConfirm.message',
          ),
          confirmLabel: this.translate.instant(
            'features.pixKeys.table.menu.disable',
          ),
          cancelLabel: this.translate.instant('common.actions.cancel'),
        },
        dismissible: true,
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.actions.onDisable!(id).subscribe({
            next: () => {},
            error: () => {},
          });
        }
      });
  }

  onDisableInView(close: unknown): void {
    if (!this.selectedKey || !this.actions.onDisable) return;
    this.loading = true;
    this.actions.onDisable(this.selectedKey.id).subscribe({
      next: () => {
        this.loading = false;
        this.actions.loadKeys?.();
        this.closeModal(close);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  getDocumentDisplay(value: string | undefined): string {
    if (!value) return 'N/A';
    return this.documentPipe.transform(value, true);
  }

  getDescriptionDisplay(value: string | undefined): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : '–';
  }

  getBankCodeDisplay(key: RecipientPixKey): string {
    const compe = key.compeCode?.trim();
    if (compe) {
      return compe;
    }
    const ispb = key.ispbCode?.trim();
    if (ispb) {
      return ispb;
    }
    return '–';
  }
}
